
/**
 * Color helper utility class
 */
export class Color {
  /**
   * Create a color from a string
   *
   * @param {String} strColor
   */
  constructor(strColor) {
    if(typeof strColor === 'object') {
      this._r = strColor.r
      this._g = strColor.g
      this._b = strColor.b

      this.strColor =
        strColor.r.toString(16) +
        strColor.g.toString(16) +
        strColor.b.toString(16)

    } else if(typeof strColor === 'string') {
      if(strColor[0] === '#') {
        strColor = strColor.slice(1)
      }

      this.strColor = strColor
    } else {
      throw new Error('invalid color format')
    }

  }

  /**
   * The red component
   *
   * @returns {Number}
   */
  get r() {
    if(!this._r) {
      this._r = parseInt(this.strColor.slice(0, 2), 16)
    }

    return this._r
  }

  /**
   * The green component
   *
   * @returns {Number}
   */
  get g() {
    if(!this._g) {
      this._g = parseInt(this.strColor.slice(2, 4), 16)
    }
    return this._g
  }

  /**
   * The blue component
   *
   * @returns {Number}
   */
  get b() {
    if(!this._b) {
      this._b = parseInt(this.strColor.slice(4, 6), 16)
    }
    return this._b
  }

  /**
   * Averages the color with the given target
   *
   * @param {Color} color
   * @param {Number} weight
   * @returns {Color}
   */
  mixWith(color, weight = 0.5) {
    var srcWeight = 1 - Math.min(weight, 1)

    var r = Math.ceil((this.r * srcWeight) + (color.r * weight)),
      g = Math.ceil((this.g * srcWeight) + (color.g * weight)),
      b = Math.ceil((this.b * srcWeight) + (color.b * weight))

    return new Color({r, g, b})
  }

  /**
   * Returns a string representation of the color with a hash prepended
   *
   * @returns {String}
   */
  toString() {
    return `#${this.strColor}`
  }

  /**
   * Returns a numeric representation of this color
   *
   * @returns {Number}
   */
  toNumber() {
    return parseInt(this.strColor, 16)
  }

  /**
   * Returns a color for the given action
   *
   * @param {Number} which
   * @returns {Color}
   */
  static action(which) {
    // try to parse the given id
    var id = parseInt(which)

    if(isNaN(id) && typeof which !== 'undefined' && which.length > 0) {
      // if it couldn't be parsed try to use the first character
      id = which.toString().charCodeAt(0)
    } else if(isNaN(id)) {
      // if there isn't any characters just pick one at random
      id = Math.floor(Math.random() * Color.colors.actions.length)
    } else {
      // if the id is negative start from the back
      id = id < 0 ? Math.abs(Color.colors.actions.length + id) : id
    }

    // ensure the id is smaller than the maximum id
    id = id % Color.colors.actions.length

    // return the color
    return new Color(Color.colors.actions[id])
  }
}


/**
 * The predefined colors to choose from
 */
Color.colors = {
  primary: {
    turqoise: ['#477b7f', '#6a8e91', '#8ea8ae'], // turquises
    red: ['#b83a37', '#c26252', '#d49584'], // reds
    gray: ['#5b5c5b', '#8c8c8d', '#d49584'], // grays
  },
  secondary: {
    purplish: ['#756267', '#742660', '#72255E', '#7D5769'], // purplish
    yellowish: ['#DCD0A1', '#CFA560', '#E1AA77', '#E7D250'], // yellowish
    redish: ['#7E313B', '#782E37'], // deeper red
    greenish: ['#818755', '#999B81', '#B2B283', '#AAAB7F', '#C1B07D'], // greenish
  },
  actions: [
    '#557b7f', '#598e8c', '#62a294', '#73b498', '#8bc698', '#aad796',
    '#cee694', '#cecc77', '#cdb15f', '#c9964e', '#c27a44', '#b95f3f',
    '#ab443e',

    '#4d687f', '#557d93', '#5e92a5', '#6aa8b5', '#78bfc3', '#8bd5cf',
    '#a3ebd9', '#9ee0c2', '#9dd4ab', '#9ec894', '#a2ba7e', '#a6ad6a',
    '#ab9e59',
  ]
}